import { useExportExtrato } from "../../lib/export/useExportExtrato";

/*
 * Botão "Exportar extrato" (Gap C — cabeçalho da Tela 1).
 *
 * Dispara o download do CSV via `useExportExtrato` (ação imperativa, não query).
 *
 * VISIBILIDADE: o export é EMPRESA-only (o backend não suporta export de grupo) e
 * gated pela ação RBAC `exportar_extrato`. Em visão grupo (`empresaId === null`)
 * OU sem permissão (`!podeExportar`), o botão NÃO renderiza. O gate de UI é
 * conveniência — o backend é o gate real (404 fora de escopo).
 *
 * JANELA RETROATIVA: o `horizonte` do Dashboard é projeção (para FRENTE), mas o
 * export é RETROATIVO (últimos N dias). O tooltip deixa isso explícito para não
 * confundir o usuário.
 *
 * Estados: idle → exportando ("Exportando…", disabled) → erro (mensagem inline
 * `role="alert"` no padrão do projeto). A mensagem é AMIGÁVEL (o hook já saneou).
 */

interface BotaoExportarExtratoProps {
  /** Empresa selecionada, ou `null` na visão de GRUPO (botão oculto). */
  empresaId: string | null;
  /** Horizonte retroativo (últimos N dias) a exportar. */
  horizonte: number;
  /** O usuário possui a ação RBAC `exportar_extrato`? */
  podeExportar: boolean;
}

export function BotaoExportarExtrato({
  empresaId,
  horizonte,
  podeExportar,
}: BotaoExportarExtratoProps): JSX.Element | null {
  const { exportando, erro, baixar } = useExportExtrato();

  // EMPRESA-only e gated por `exportar_extrato`: grupo ou sem permissão → oculto.
  if (empresaId === null || !podeExportar) {
    return null;
  }

  function aoClicar(): void {
    if (empresaId !== null) {
      void baixar(empresaId, horizonte);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={aoClicar}
        disabled={exportando}
        title={`Exporta os últimos ${horizonte} dias`}
        className="rounded bg-flame px-4 py-2 font-corpo text-base text-snow hover:opacity-90 disabled:opacity-60"
      >
        {exportando ? "Exportando…" : "Exportar extrato"}
      </button>

      {erro !== null && (
        <p role="alert" className="font-corpo text-sm text-erro">
          {erro}
        </p>
      )}
    </div>
  );
}
